/**
 * In-memory workspace store.
 *
 * Holds Concepts, Relations, and Diagrams keyed by id. Exposes a small
 * mutation API and a subscribe model. The web client wraps this in a Zustand
 * store; the Worker uses it for snapshot reconstitution and validation.
 *
 * Mutations bump a monotonic `revision` counter so subscribers can detect
 * changes without diffing. Each subscriber gets called once per `commit()`,
 * not once per individual mutation, so bulk operations are cheap.
 */
import type { Concept, Diagram, Relation, ViewElementState } from "./types";

export interface WorkspaceState {
  concepts: Map<string, Concept>;
  relations: Map<string, Relation>;
  diagrams: Map<string, Diagram>;
  revision: number;
}

export type Subscriber = (state: WorkspaceState) => void;

export class WorkspaceStore {
  private state: WorkspaceState = {
    concepts: new Map(),
    relations: new Map(),
    diagrams: new Map(),
    revision: 0,
  };

  private readonly subscribers = new Set<Subscriber>();
  private inTransaction = false;
  private dirtyDuringTransaction = false;

  /**
   * Forceer een revision-bump + subscriber-notify zonder specifieke state-
   * mutatie. Voor callers die willen dat React re-rendert na iets buiten de
   * normale mutatie-API (of gewoon expliciet een refresh willen forceren).
   * Gaat door dezelfde `markDirty`-weg als elke echte mutatie — dit is de
   * ENIGE juiste manier om de revision te verhogen. Schrijf NOOIT
   * rechtstreeks naar de Zustand-`revision`-spiegel (bv. via
   * `set(s => ({revision: s.revision + 1}))`): die spiegel wordt uitsluitend
   * gevuld door de `subscribe`-sync onderaan dit bestand, en een parallelle
   * schrijver met een eigen (mogelijk stale) snapshot kan met deze teller
   * botsen — als beide toevallig dezelfde waarde bereiken, ziet de subscriber
   * geen verschil en slaat 'm de sync over, waardoor React niet re-rendert
   * ondanks een echte mutatie. Precies dit veroorzaakte de "vereenvoudigde-
   * kopie-object-springt-terug"-bug (2026-07-11): `handleDiagramBranchCopy`
   * deed zowel een echte store-mutatie (correct gesynced) ALS een losse
   * `revision: s.revision + 1` op een stale snapshot in hetzelfde
   * `set()`-object, die de correcte waarde weer overschreef.
   */
  forceRevisionBump(): void {
    this.markDirty();
  }

  // ---- Read API ----------------------------------------------------------

  getConcept(id: string): Concept | undefined {
    return this.state.concepts.get(id);
  }

  getRelation(id: string): Relation | undefined {
    return this.state.relations.get(id);
  }

  getDiagram(id: string): Diagram | undefined {
    return this.state.diagrams.get(id);
  }

  listConcepts(): Concept[] {
    return [...this.state.concepts.values()];
  }

  listRelations(): Relation[] {
    return [...this.state.relations.values()];
  }

  listDiagrams(): Diagram[] {
    return [...this.state.diagrams.values()];
  }

  /** Read-only snapshot of the current state. Subscribers receive this. */
  snapshot(): WorkspaceState {
    return this.state;
  }

  // ---- Write API ---------------------------------------------------------

  upsertConcept(concept: Concept): void {
    this.state.concepts.set(concept.id, concept);
    this.markDirty();
  }

  /**
   * De versie die de server terugmeldde na een schrijfactie vasthouden.
   *
   * Apart van `upsertConcept` en bewust **zonder** `markDirty`: er verandert
   * niets aan wat de gebruiker ziet, alleen aan wat de volgende schrijfactie
   * meestuurt. Zou dit als gewone mutatie tellen, dan hertekende het canvas
   * bij elke toetsaanslag mee.
   *
   * Zonder deze stap blijft de client op de versie van z'n laatste ophaalactie
   * staan en weigert de laag z'n tweede schrijfactie op hetzelfde object —
   * zie `docs/architecture/gelijktijdigheid.md` §4.
   */
  noteRowVersion(kind: "concept" | "relation" | "diagram", id: string, version: number): void {
    const bak =
      kind === "concept"
        ? this.state.concepts
        : kind === "relation"
          ? this.state.relations
          : this.state.diagrams;
    const huidig = (bak as Map<string, { rowVersion?: number }>).get(id);
    if (!huidig || huidig.rowVersion === version) return;
    (bak as Map<string, unknown>).set(id, { ...huidig, rowVersion: version });
  }

  /**
   * De entry-versies die de server terugmeldde na een diagram-schrijfactie.
   *
   * Zelfde reden als `noteRowVersion`, maar een laag dieper: de teller per
   * voorkomen wordt door `viewStateMerge` opgehoogd en niet door een trigger,
   * dus zonder terugmelding houdt de client de stand van z'n laatste
   * ophaalactie vast. Zodra de snelle route vervalt — een collega schrijft aan
   * hetzelfde diagram — wordt dan élk voorkomen geweigerd.
   *
   * Alleen het versieveld; de rest van de entry blijft zoals 'ie lokaal staat,
   * want tijdens de round-trip kan de gebruiker alweer verder gesleept hebben.
   * Geen `markDirty`: er verandert niets aan wat je ziet.
   */
  noteViewStateVersions(diagramId: string, versies: ReadonlyMap<string, number>): void {
    const diagram = this.state.diagrams.get(diagramId);
    if (!diagram || versies.size === 0) return;
    let gewijzigd = false;
    const volgende: Record<string, unknown> = { ...diagram.viewState };
    for (const [sleutel, versie] of versies) {
      const entry = volgende[sleutel] as { version?: number } | undefined;
      if (!entry || typeof entry !== "object" || entry.version === versie) continue;
      volgende[sleutel] = { ...entry, version: versie };
      gewijzigd = true;
    }
    if (!gewijzigd) return;
    this.state.diagrams.set(diagramId, {
      ...diagram,
      viewState: volgende as typeof diagram.viewState,
    });
  }

  removeConcept(id: string): void {
    if (this.state.concepts.delete(id)) {
      // Cascade: drop any relation that references this concept as endpoint.
      for (const [relId, rel] of this.state.relations) {
        if (rel.sourceConceptId === id || rel.targetConceptId === id) {
          this.state.relations.delete(relId);
        }
      }
      this.markDirty();
    }
  }

  upsertRelation(relation: Relation): void {
    this.state.relations.set(relation.id, relation);
    this.markDirty();
  }

  removeRelation(id: string): void {
    if (this.state.relations.delete(id)) {
      this.markDirty();
    }
  }

  upsertDiagram(diagram: Diagram): void {
    this.state.diagrams.set(diagram.id, diagram);
    this.markDirty();
  }

  removeDiagram(id: string): void {
    if (this.state.diagrams.delete(id)) {
      this.markDirty();
    }
  }

  /** Update the view-state of a diagram for a single element. */
  setViewElementState(diagramId: string, elementId: string, state: ViewElementState): void {
    const diagram = this.state.diagrams.get(diagramId);
    if (!diagram) {
      return;
    }
    diagram.viewState[elementId] = state;
    this.markDirty();
  }

  /**
   * Remove a single element entirely from a diagram's viewState. Differs
   * from setting `hidden: true`: the entry vanishes from the map. Used by
   * cut/paste-undo so a paste-batch can be cleanly reversed without
   * leaving "ghost" viewState-entries with hidden flags behind.
   */
  removeViewElementState(diagramId: string, elementId: string): void {
    const diagram = this.state.diagrams.get(diagramId);
    if (!diagram) {
      return;
    }
    if (diagram.viewState[elementId] !== undefined) {
      delete diagram.viewState[elementId];
      this.markDirty();
    }
  }

  /** Replace whole concept set (used for snapshot restore). */
  replace(concepts: Concept[], relations: Relation[], diagrams: Diagram[]): void {
    this.state.concepts = new Map(concepts.map((c) => [c.id, c]));
    this.state.relations = new Map(relations.map((r) => [r.id, r]));
    this.state.diagrams = new Map(diagrams.map((d) => [d.id, d]));
    this.markDirty();
  }

  // ---- Transactions ------------------------------------------------------

  /**
   * Group multiple mutations into a single notification. Subscribers see
   * exactly one update at the end of the transaction, with a single revision
   * bump. Useful for undo (one command = one transaction = one notification).
   */
  transaction(fn: () => void): void {
    if (this.inTransaction) {
      // Nested transactions just inherit the outer one.
      fn();
      return;
    }
    this.inTransaction = true;
    this.dirtyDuringTransaction = false;
    try {
      fn();
    } finally {
      this.inTransaction = false;
      if (this.dirtyDuringTransaction) {
        this.bumpAndNotify();
      }
    }
  }

  // ---- Subscribers -------------------------------------------------------

  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn);
    return () => {
      this.subscribers.delete(fn);
    };
  }

  // ---- Internal ----------------------------------------------------------

  private markDirty(): void {
    if (this.inTransaction) {
      this.dirtyDuringTransaction = true;
    } else {
      this.bumpAndNotify();
    }
  }

  private bumpAndNotify(): void {
    this.state.revision += 1;
    for (const fn of this.subscribers) {
      fn(this.state);
    }
  }
}
